import React, { useEffect, useMemo, useState } from "react";

import { API, graphqlOperation } from "aws-amplify";

import {
  Card,
  Divider,
  Grid,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  CreateButton,
  Datagrid,
  EditButton,
  FunctionField,
  ListContextProvider,
  RefreshButton,
  ShowButton,
  SimpleList,
  TextField,
  Title,
  TopToolbar,
  useList,
  useNotify,
  useVersion,
} from "react-admin";

import { reloadYardCarrierUsersByCarrier } from "../../../graphql/carrier/carrier-queries";
import { UserStatusIcon } from "../../../managerApp/resources/users/UserStatusIcon";
import { ChevronRightIcon } from "../../../components/common/iconography";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { SearchField } from "../../../components/common/SearchField";
import { Pagination } from "../../../components/Pagination";
import { useUser } from "../../../authenticatorApp/UserContext";
import { logError } from "../../../utils/appUtils";
import {
  carrierMembershipKey,
  carrierMembershipScope,
  getUsableCarrierJunctions,
  normalizeCarrierUserType,
} from "../../../utils/carrierUserUtils";
import {
  AclType,
  CarrierUserTypeChoices,
  UserType,
} from "../../../utils/constants";
import { Empty } from "../../../components/common/Empty";

const useStyles = makeStyles((theme) => ({
  accountField: {
    maxWidth: "16rem",
    minWidth: "16rem",
  },
  statusIcon: {
    marginTop: theme.spacing(1),
  },
}));

const renderName = (record) => {
  return `${record.lastName}, ${record.firstName}`;
};

// Same split as the manager's Carrier Users list: portal colleagues on one
// tab, gate-only drivers on the other. Dispatch is "not a driver" rather than
// an equality check, so legacy null types and any unrecognised value land with
// their portal peers.
const tabs = [
  { id: "dispatch", name: "Dispatch" },
  { id: "drivers", name: "Drivers" },
];

const isDriverPerson = (person) =>
  normalizeCarrierUserType(person.type) === UserType.CARRIER_DRIVER;

const getSessionTab = () => {
  const sessionTab = Number(sessionStorage.getItem("CarrierAppUsersTab"));
  return sessionTab && tabs[sessionTab] ? sessionTab : 0;
};

// Inviting a colleague is worth the toolbar on a phone, where searching and
// refreshing are not — a dispatcher signing someone up is often doing it away
// from a desk. Create only invites dispatch users, so `canCreate` is false on
// the Drivers tab: drivers create their own profile in the Driver App.
const ListActions = ({ isXSmall, canCreate, onSearch }) => {
  if (isXSmall && !canCreate) return null;

  return (
    <Grid container justify="space-between" alignItems="center">
      <Grid item>
        {!isXSmall && (
          <SearchField
            id="searchTerm"
            label="Search"
            margin="dense"
            variant="outlined"
            handleSearch={onSearch}
          />
        )}
      </Grid>
      <Grid item>
        <TopToolbar>
          {canCreate && <CreateButton basePath="/carrierUsers" />}
          {!isXSmall && <RefreshButton />}
        </TopToolbar>
      </Grid>
    </Grid>
  );
};

// One page of junction rows per carrier entry, followed through nextToken:
// the collapse to people and the paging both happen here, over the whole set.
const fetchCarrierUserJunctions = async (carrierIds) => {
  const pages = await Promise.all(
    carrierIds.map(async (carrierId) => {
      const rows = [];
      let nextToken;
      do {
        const response = await API.graphql(
          graphqlOperation(reloadYardCarrierUsersByCarrier, {
            carrierId,
            active: { eq: "true" },
            limit: 500,
            nextToken,
          }),
        );
        const page = response.data.reloadYardCarrierByCarrierByActive;
        rows.push(...(page?.items ?? []));
        nextToken = page?.nextToken;
      } while (nextToken);
      return rows;
    }),
  );
  return pages.flat();
};

export const CarrierUserList = (props) => {
  const { appUser, isAuthorized } = useUser();
  const classes = useStyles();
  const notify = useNotify();
  const version = useVersion();
  const isXSmall = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const lteMedium = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const canManageUsers = isAuthorized(AclType.USERS);

  const [junctionRows, setJunctionRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [tabIndex, setTabIndex] = useState(getSessionTab());

  const actorJunctions = appUser?.reloadYardCarriers?.items;

  // A Carrier record is one company's entry at one yard, so "this dispatcher's
  // users" means one query per entry they hold live standing under, merged. A
  // single entry — the session's — was queried here before, which showed only
  // the yard being worked in and hid every colleague beyond it.
  const carrierIds = useMemo(
    () => [
      ...new Set(
        getUsableCarrierJunctions(actorJunctions).usable.map(
          (junction) => junction.carrierId,
        ),
      ),
    ],
    [actorJunctions],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFetchFailed(false);
    fetchCarrierUserJunctions(carrierIds)
      .then((rows) => {
        if (cancelled) return;
        setJunctionRows(rows);
        setLoaded(true);
      })
      .catch((error) => {
        if (cancelled) return;
        logError("carrierApp:carrierUsers:CarrierUserList:fetch", error, notify);
        // Without this the empty state renders instead: a dispatcher whose
        // fetch failed was told their carrier has no colleagues, with a Create
        // button as the suggested next step. `loaded` also has to flip, or
        // useList's processing effect early-returns forever and the list never
        // recovers even once rows arrive.
        setFetchFailed(true);
        setLoaded(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, carrierIds]);

  // Which of a colleague's junctions gets to speak for them on their one line.
  // The transload being worked in comes first, since its block is the one that
  // explains why they are refused here. Failing that, a yard the acting user
  // stands in themselves — that membership is one they administer, so its type
  // and block are the ones the profile will show. Anything else is a yard this
  // list can see but say nothing about, and only wins if nothing better exists.
  const actorScope = useMemo(
    () => carrierMembershipScope(actorJunctions),
    [actorJunctions],
  );

  const currentKey = carrierMembershipKey({
    reloadYardId: appUser?.reloadYardId,
    carrierId: appUser?.carrierId,
  });

  // A colleague working in several transloads has a junction row for each, and
  // the list shows one line per person. Collapsed before anything paginates:
  // paging the raw rows let one person hold several slots on a page, reappear
  // on the next one, and leave the row count claiming more people than shown.
  const people = useMemo(() => {
    const rankOf = (record) => {
      const key = carrierMembershipKey(record);
      if (key === currentKey) return 2;
      return actorScope.has(key) ? 1 : 0;
    };

    const byUser = {};
    const ranks = {};
    const order = [];
    junctionRows.forEach((record) => {
      if (!record?.user) return;
      // Keyed by the CarrierUser id so EditButton routes to
      // `/carrierUsers/{userId}` (CarrierEdit edits the user). The best-ranked
      // row wins, so the line never depends on the order rows came back in.
      const claimed = byUser[record.user.id];
      const rank = rankOf(record);
      if (claimed && ranks[record.user.id] >= rank) return;

      byUser[record.user.id] = {
        ...record.user,
        businessName: record.businessName,
        type: record.type,
        reloadYardId: record.reloadYardId,
        blockAccess: record.blockAccess,
      };
      ranks[record.user.id] = rank;
      if (!claimed) order.push(record.user.id);
    });
    return order.map((id) => byUser[id]);
  }, [junctionRows, actorScope, currentKey]);

  const isDrivers = tabs[tabIndex].id === "drivers";

  const tabbed = useMemo(
    () => people.filter((person) => isDriverPerson(person) === isDrivers),
    [people, isDrivers],
  );

  const searched = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return tabbed;
    return tabbed.filter((person) =>
      [
        person.lastName,
        person.firstName,
        person.email,
        // What identifies a driver — they have no email to search by.
        person.phone,
        person.driverCode,
      ].some((field) =>
        String(field ?? "")
          .toLowerCase()
          .includes(term),
      ),
    );
  }, [tabbed, searchTerm]);

  // memorize perPage settings
  // see useEffect [listContext?.perPage]) below
  const sessionPerPage = localStorage.getItem(`${props.resource}_perPage`);
  const [defaultPerPage, setDefaultPerPage] = useState(
    sessionPerPage ? Number(sessionPerPage) : 10,
  );

  const listContext = useList({
    data: searched,
    ids: searched.map((person) => person.id),
    loading,
    loaded,
    perPage: defaultPerPage,
    sort: { field: "lastName", order: "ASC" },
  });

  useEffect(() => {
    if (listContext?.perPage) {
      localStorage.setItem(`${props.resource}_perPage`, listContext.perPage);
      setDefaultPerPage(Number(listContext.perPage));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listContext?.perPage]);

  return (
    <ListContextProvider
      value={{
        ...listContext,
        basePath: "/carrierUsers",
        resource: "carrierUsers",
      }}
    >
      <Title title={<ForceTitle title="Users" />} />
      <ListActions
        isXSmall={isXSmall}
        canCreate={canManageUsers && !isDrivers}
        onSearch={(term) => {
          setSearchTerm(term);
          listContext.setPage(1);
        }}
      />
      <Card>
        <Tabs
          value={tabIndex}
          indicatorColor="primary"
          textColor="primary"
          onChange={(event, value) => {
            sessionStorage.setItem("CarrierAppUsersTab", value);
            setTabIndex(value);
            // useList pages client-side and keeps `page` in its own state, so a
            // stale page survives the switch: paged to 3, then onto a tab with
            // four people, the slice is empty while the footer still claims
            // four rows. The empty state does not catch it either — it tests
            // the unpaginated count.
            listContext.setPage(1);
          }}
        >
          {tabs.map((tab, index) => (
            <Tab value={index} key={tab.id} label={tab.name} />
          ))}
        </Tabs>
        <Divider />
        {!loading && fetchFailed ? (
          <Typography
            variant="body2"
            color="error"
            style={{ padding: "1rem 1.5rem" }}
          >
            We couldn't load your users. Check your connection and refresh — this
            is not a sign that your carrier has no users.
          </Typography>
        ) : !loading && searched.length === 0 ? (
          <Empty
            basePath="/carrierUsers"
            resource="carrierUsers"
            // Drivers are not invited from here — they create their own
            // profile in the Driver App — so their empty tab offers no Create.
            hasCreate={!isDrivers && canManageUsers}
            message={
              isDrivers
                ? "No drivers enrolled yet — drivers create their profile in the Driver App"
                : "Invite a dispatch colleague"
            }
          />
        ) : (
          <CarrierGrid
            appUser={appUser}
            canManageUsers={canManageUsers}
            isXSmall={isXSmall}
            lteMedium={lteMedium}
            classes={classes}
            isDrivers={isDrivers}
          />
        )}
        <Pagination {...listContext} />
      </Card>
    </ListContextProvider>
  );
};

// Your own profile is always yours to edit; a colleague's is only editable by a
// user who manages users, and read-only to everyone else.
const canEditProfile = (record, appUser, canManageUsers) => {
  return record.id === appUser.id || canManageUsers;
};

// SimpleList calls linkType as (record, id); Datagrid calls rowClick as
// (id, basePath, record). Both end up here, so each caller has to pick the
// record out of its own argument list — reading the third argument on a phone
// passed undefined and took the list down.
const rowClickHandler = (record, appUser, canManageUsers) => {
  return canEditProfile(record, appUser, canManageUsers) ? "edit" : "show";
};

const renderRole = (record) =>
  CarrierUserTypeChoices.find(
    (choice) => choice.id === normalizeCarrierUserType(record.type),
  )?.name ?? "";

const CarrierGrid = (props) => {
  const { appUser, canManageUsers, isXSmall, lteMedium, classes, isDrivers } =
    props;

  return (
    <>
      {isXSmall ? (
        <SimpleList
          primaryText={renderName}
          secondaryText={(record) =>
            isDrivers ? record.driverCode || record.phone : record.email
          }
          tertiaryText={(record) => record.accountName}
          linkType={(record) => rowClickHandler(record, appUser, canManageUsers)}
          leftIcon={(record) => <UserStatusIcon record={record} />}
          rightIcon={() => (
            <ChevronRightIcon
              fontSize="large"
              color="secondary"
              style={{ marginLeft: 40, marginTop: 10 }}
            />
          )}
        />
      ) : lteMedium ? (
        <Datagrid
          rowClick={(jsonRecord, basePath, record) =>
            rowClickHandler(record, appUser, canManageUsers)
          }
        >
          <FunctionField
            source="lastName"
            label="Name"
            render={renderName}
            sortable={true}
          />
          <UserStatusIcon
            source="status"
            label="Status"
            classes={classes.statusIcon}
            sortable={false}
          />
          {isDrivers ? (
            <TextField source="driverCode" label="Driver code" sortable={false} />
          ) : (
            <TextField source="email" label="Email" sortable={false} />
          )}
        </Datagrid>
      ) : (
        <Datagrid
          rowClick={(jsonRecord, basePath, record) =>
            rowClickHandler(record, appUser, canManageUsers)
          }
        >
          <FunctionField
            source="lastName"
            label="Name"
            render={renderName}
            sortable={true}
          />
          <UserStatusIcon
            source="status"
            label="Status"
            classes={classes.statusIcon}
            sortable={false}
          />
          {isDrivers ? (
            <TextField source="driverCode" label="Driver code" sortable={false} />
          ) : (
            <FunctionField
              source="type"
              label="Role"
              render={renderRole}
              sortable={false}
            />
          )}
          {!isDrivers && (
            <TextField source="email" label="Email" sortable={false} />
          )}
          <TextField source="phone" label="Phone" sortable={false} />
          <FunctionField
            render={(record) =>
              canEditProfile(record, appUser, canManageUsers) ? (
                <EditButton record={record} />
              ) : (
                <ShowButton record={record} />
              )
            }
          />
        </Datagrid>
      )}
    </>
  );
};
